'use client'

import { useState, useEffect } from 'react'
import { cn } from '@/lib/utils'
import {
  HeroProjectFragment,
  HeroExhibitionFragment,
  ProjectVerticalDetailFragment,
  Render_Types_Enum,
  Enum_Exhibition_Type
} from '@/gql/generated/graphql'
import { DetailText, DisplayText } from 'design-configuration/components'
import { AllPurposeLink } from '@components/all-purpose-link'
import { projectPurchaseRoute, projectShowRoute, exhibitionShowRoute } from '@utils/routes'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { BentoVideo } from './bento-item'
import { getProjectStatus, getVerticalTitle, ValidStatusType } from '@utils/project'
import { buttonVariants } from '@components/button'
import { trackButtonClicked, TrackedButtonNames } from '@utils/analytics'
import { STATUS_REFRESH_INTERVAL_SECONDS, useProjectStatus } from '@/hooks/use-project-status'

export interface HeroSlideshowManualItem {
  title: string
  subtitle: string
  mediaUrl: string
  type: 'manual'
  link: string
  ctaTitle: string
}

interface HeroItemsProps {
  items: (HeroProjectFragment | HeroExhibitionFragment | HeroSlideshowManualItem)[]
}

export function HeroSlideshow({ items }: HeroItemsProps): React.ReactNode {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [progress, setProgress] = useState(0)
  const [isControlsHovered, setIsControlsHovered] = useState(false)

  const totalItems = items.length

  // Auto-advance slideshow every 6 seconds
  useEffect(() => {
    if (totalItems <= 1) return

    const duration = 6000 // 6 seconds
    let startTime: number

    if (isControlsHovered) {
      // When hovering controls, back up progress by 1 second
      const currentProgress = progress
      const timeElapsed = currentProgress * duration
      const adjustedTime = Math.max(timeElapsed - 1000, 0)
      setProgress(adjustedTime / duration)
      return
    }

    // Initialize startTime based on current progress
    startTime = Date.now() - progress * duration

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime
      const newProgress = Math.min(elapsed / duration, 1)
      setProgress(newProgress)

      if (elapsed >= duration) {
        setCurrentIndex((current) => (current + 1) % totalItems)
        setProgress(0)
        startTime = Date.now()
      }
    }, 16)

    return () => clearInterval(interval)
  }, [totalItems, currentIndex, isControlsHovered])

  if (!totalItems) return null

  return (
    <div className="rounded-xs relative h-[650px] w-full overflow-hidden">
      {items.map((item, index) => {
        const isProject = 'featured_token' in item
        const isManual = 'type' in item && item.type === 'manual'

        if (isManual) {
          return <HeroSlideshowManualItem key={index} item={item} index={index} currentIndex={currentIndex} />
        }

        return isProject ? (
          <ProjectSlide
            key={item.id}
            project={item}
            index={index}
            currentIndex={currentIndex}
            hoverChange={setIsControlsHovered}
          />
        ) : (
          <ExhibitionSlide
            key={(item as HeroExhibitionFragment).slug}
            exhibition={item as HeroExhibitionFragment}
            index={index}
            currentIndex={currentIndex}
          />
        )
      })}

      {/* Navigation dots */}
      {totalItems > 1 && (
        <div
          className="right-md top-md absolute hidden sm:block"
          onMouseEnter={() => setIsControlsHovered(true)}
          onMouseLeave={() => setIsControlsHovered(false)}
        >
          <div className="flex gap-2">
            {items.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setCurrentIndex(index)
                  setProgress(0)
                }}
                className={cn(
                  'relative h-2 overflow-hidden rounded-full transition-all',
                  index === currentIndex ? 'w-6' : 'w-2'
                )}
                aria-label={`Go to slide ${index + 1}`}
              >
                <div className="bg-surface-60 hover:bg-surface-90 absolute inset-0" />
                {index === currentIndex && (
                  <div
                    className="bg-surface absolute inset-0 origin-left transition-transform"
                    style={{ transform: `scaleX(${progress})` }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const HeroSlideshowManualItem = ({
  item,
  index,
  currentIndex
}: {
  item: HeroSlideshowManualItem
  index: number
  currentIndex: number
}): React.ReactNode => {
  const isActive = index === currentIndex
  return (
    <div
      key={item.title}
      className={cn(
        'absolute inset-0 transition-opacity duration-1000',
        isActive ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
      )}
    >
      <AllPurposeLink href={item?.link} className="block h-full w-full">
        {/* Media slide */}
        <div className="relative h-full w-full">
          <ImageWithCustomLoader
            src={item.mediaUrl}
            alt={item.title || 'Hero project'}
            fill
            className={cn('object-cover transition-transform duration-300')}
            priority={index === 0}
            sizes="100vw"
          />

          {/* Overlay gradient */}
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(85.77% 70.71% at 50% 50%, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.70) 100%)',
              boxShadow: '3px 3px 6px 0px rgba(0, 0, 0, 0.20)'
            }}
          />
        </div>

        {/* Project info */}
        <div className="text-on-surface-inverted p-md absolute left-0 top-0 w-full">
          <DisplayText className="mt-2xs font-medium">{item.title}</DisplayText>
          <DisplayText className="font-light">{item.subtitle}</DisplayText>
        </div>
      </AllPurposeLink>
      <div className="right-md bottom-md absolute flex gap-2">
        <AllPurposeLink
          href={item.link}
          className={cn(buttonVariants({ colorTheme: 'inverted-primary', branded: true }))}
        >
          {item.ctaTitle}
        </AllPurposeLink>
      </div>
    </div>
  )
}

const ProjectSlide = ({
  project,
  index,
  currentIndex,
  hoverChange
}: {
  project: HeroProjectFragment
  index: number
  currentIndex: number
  hoverChange: (bool: boolean) => void
}): React.ReactNode => {
  const isActive = index === currentIndex
  const mediaUrl = project?.featured_token?.[0]?.media_url
  const isVideo = showVideo(project)
  const { status, refetchStatus } = useProjectStatus(project)
  const [isBgHovered, setIsBgHovered] = useState(false)

  useEffect(() => {
    const interval = setInterval(() => {
      refetchStatus()
    }, STATUS_REFRESH_INTERVAL_SECONDS * 1000)

    if (status === ValidStatusType.complete) {
      clearInterval(interval)
      return
    }

    return () => clearInterval(interval)
  }, [refetchStatus, status])

  return (
    <div
      key={project.id}
      className={cn(
        'absolute inset-0 transition-opacity duration-1000',
        isActive ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
      )}
    >
      <AllPurposeLink
        href={projectShowRoute(project.slug)}
        className="block h-full w-full"
        onMouseEnter={() => setIsBgHovered(true)}
        onMouseLeave={() => setIsBgHovered(false)}
        onClick={() => {
          trackButtonClicked(TrackedButtonNames.heroProject, {
            project: project
          })
        }}
      >
        {/* Media slide */}
        <div className="relative h-full w-full">
          {isVideo ? (
            <>
              {/* Show video only on larger screens (sm and up) */}
              <div className="hidden sm:block">
                <BentoVideo
                  src={project.featured_token?.[0]?.video?.url || ''}
                  className="h-full w-full"
                  videoStyle="object-cover object-center"
                  isHovered={isBgHovered}
                  muted={true}
                  controls={false}
                />
              </div>
              {/* Show image on mobile/small screens */}
              <div className="block sm:hidden">
                {mediaUrl && (
                  <ImageWithCustomLoader
                    src={mediaUrl}
                    alt={project.name || 'Hero project'}
                    fill
                    className={cn('object-cover transition-transform duration-300')}
                    priority={index === 0}
                    sizes="100vw"
                  />
                )}
              </div>
            </>
          ) : (
            mediaUrl && (
              <ImageWithCustomLoader
                src={mediaUrl}
                alt={project.name || 'Hero project'}
                fill
                className={cn('object-cover transition-transform duration-300')}
                priority={index === 0}
                sizes="100vw"
              />
            )
          )}
          {/* Overlay gradient */}
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(85.77% 70.71% at 50% 50%, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.70) 100%)',
              boxShadow: '3px 3px 6px 0px rgba(0, 0, 0, 0.20)'
            }}
          />
        </div>

        {/* Project info */}
        <div className="text-on-surface-inverted p-md absolute left-0 top-0 w-full">
          <BentoStatusTag projectStatus={status} project={project} />
          <DisplayText className="mt-2xs font-medium">{project.name}</DisplayText>
          <DisplayText className="font-light">{project.artist_name}</DisplayText>
        </div>
      </AllPurposeLink>
      {status === ValidStatusType.live && (
        <div
          className="right-md bottom-md absolute flex gap-2"
          onMouseEnter={() => hoverChange(true)}
          onMouseLeave={() => hoverChange(false)}
        >
          <ProjectPurchaseButton project={project} />
        </div>
      )}
    </div>
  )
}

const showVideo = (project: HeroProjectFragment) => {
  return (
    (project.primary_render_type === Render_Types_Enum.Video ||
      (project.primary_render_type === Render_Types_Enum.Live &&
        project.preview_render_type !== Render_Types_Enum.Image)) && // covers the case of accidental/unwanted video renders
    project.featured_token?.[0]?.video_id
  )
}

const BentoStatusTag = ({
  projectStatus,
  project
}: {
  projectStatus: ValidStatusType
  project: ProjectVerticalDetailFragment
}) => {
  if (projectStatus === ValidStatusType.complete) {
    return null
  }
  return (
    <div className="bg-primary-80 gap-x-xs px-xs flex w-fit items-center rounded-full py-[2px] font-medium uppercase">
      {projectStatus === ValidStatusType.live && <LiveDot />}
      <DetailText>
        {projectStatus}
        {projectStatus === ValidStatusType.live ? ` from ${getVerticalTitle(project)}` : ''}
      </DetailText>
    </div>
  )
}

export const LiveDot = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
    <g filter="url(#filter0_d_298_1675)">
      <circle cx="6" cy="6" r="2" fill="#03FD00" />
    </g>
    <defs>
      <filter
        id="filter0_d_298_1675"
        x="0"
        y="0"
        width="12"
        height="12"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset />
        <feGaussianBlur stdDeviation="2" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix type="matrix" values="0 0 0 0 0.0117647 0 0 0 0 0.992157 0 0 0 0 0 0 0 0 1 0" />
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_298_1675" />
        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_298_1675" result="shape" />
      </filter>
    </defs>
  </svg>
)

const ExhibitionSlide = ({
  exhibition,
  index,
  currentIndex
}: {
  exhibition: HeroExhibitionFragment
  index: number
  currentIndex: number
}): React.ReactNode => {
  const isActive = index === currentIndex
  const mediaUrl = exhibition.HeaderImage?.data?.attributes?.url

  const project1 = exhibition.Collections[0]?.Collection?.Project ?? undefined
  const project2 = exhibition.Collections[1]?.Collection?.Project ?? undefined
  const project1Status = getProjectStatus(project1)
  const project2Status = getProjectStatus(project2)

  let cta = (
    <AllPurposeLink
      href={exhibitionShowRoute(exhibition.slug)}
      className={cn(buttonVariants({ colorTheme: 'inverted-primary', branded: true }))}
      onClick={() => {
        trackButtonClicked(TrackedButtonNames.heroExhibition, { exhibition: exhibition })
      }}
    >
      View Exhibition
    </AllPurposeLink>
  )
  if (project1Status === ValidStatusType.live) {
    cta = <ProjectPurchaseButton project={project1} />
  } else if (project2Status === ValidStatusType.live) {
    cta = <ProjectPurchaseButton project={project2} />
  }

  return (
    <div
      key={exhibition.slug}
      className={cn(
        'absolute inset-0 transition-opacity duration-1000',
        isActive ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
      )}
    >
      <AllPurposeLink
        href={exhibitionShowRoute(exhibition.slug)}
        className="block h-full w-full"
        onClick={() => {
          trackButtonClicked(TrackedButtonNames.heroExhibition, { exhibition: exhibition })
        }}
      >
        {/* Media slide */}
        <div className="relative h-full w-full">
          {mediaUrl && (
            <ImageWithCustomLoader
              src={mediaUrl}
              alt={exhibition.Title || 'Hero exhibition'}
              fill
              className={cn('object-cover transition-transform duration-300')}
              priority={index === 0}
              sizes="100vw"
            />
          )}
          {/* Overlay gradient */}
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(85.77% 70.71% at 50% 50%, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.70) 100%)',
              boxShadow: '3px 3px 6px 0px rgba(0, 0, 0, 0.20)'
            }}
          />
        </div>

        {/* Exhibition info */}
        <div className="text-on-surface-inverted p-md absolute left-0 top-0 w-full">
          <div className="bg-primary-80 gap-x-xs px-xs flex w-fit items-center rounded-full py-[2px] font-medium uppercase">
            <DetailText>Exhibition</DetailText>
          </div>
          <DisplayText className="mt-2xs font-medium">{exhibition.Title}</DisplayText>
          <DisplayText className="font-light">
            {(exhibition.Type === Enum_Exhibition_Type.Group
              ? exhibition.Collections
              : exhibition.Collections.slice(0, 1)
            )
              .map((project) => project?.Collection?.Project?.artist_name)
              .join(', ') ?? ''}
          </DisplayText>
        </div>
      </AllPurposeLink>
      <div className="right-md bottom-md absolute flex gap-2">{cta}</div>
    </div>
  )
}

const ProjectPurchaseButton = ({ project }: { project?: { project_id: string; slug: string } }): React.ReactNode => {
  if (!project) return null
  return (
    <AllPurposeLink
      href={projectPurchaseRoute(project.slug)}
      className={cn(buttonVariants({ colorTheme: 'inverted-primary', branded: true }))}
      onClick={() => {
        trackButtonClicked(TrackedButtonNames.heroProjectPurchase, {
          project: project
        })
      }}
    >
      Purchase
    </AllPurposeLink>
  )
}
